import {
	BasicButton,
	FloatingLoader,
	SearchInput,
	Skeleton,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useContactPeoples from "@/hooks/useContactPeoples";
import { useDebounce } from "@/hooks/useDebounce";
import useUpdateGroup from "@/hooks/useUpdateGroup";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError } from "@/libraries/helper";
import { getGroupQueryOptions, getMembersQueryOptions } from "@/queries";
import EmptyPeoples from "@/routes/dashboard/contacts/-components/EmptyPeoples";
import type { GetGroupResponse } from "@/types";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import Avatar from "@mui/material/Avatar";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import {
	type ErrorComponentProps,
	createFileRoute,
} from "@tanstack/react-router";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/chat/group/$id/members")({
	loader: async ({ context, params }) => {
		let groupData: GetGroupResponse | null = null;
		let peoplesData = null;
		if (onlineManager.isOnline() && !context.isRestoring) {
			groupData = await context.queryClient.ensureQueryData(
				getGroupQueryOptions(params.id),
			);
			peoplesData = await context.queryClient.ensureInfiniteQueryData(
				getMembersQueryOptions({
					directory: groupData?.group?.directory ?? false,
				}),
			);
		}
		groupData = context.queryClient.getQueryData([
			QUERY_KEYS.GET_GROUP,
			params.id,
		]);
		peoplesData = context.queryClient.getQueryData([
			QUERY_KEYS.GET_MEMBERS,
			{
				directory: groupData?.group?.directory ?? false,
			},
		]);
		return [groupData, peoplesData];
	},
	component: RouteComponent,
	errorComponent: ErrorComponent,
});

function ErrorComponent({ error }: ErrorComponentProps) {
	const navigate = Route.useNavigate();
	if (isAxiosError(error, 403)) {
		return (
			<>
				<BackAppBar title="Add Member">
					<div className="container space-y-4">
						<Typography fontWeight={700} fontSize={14}>
							Contacts
						</Typography>
					</div>
				</BackAppBar>
				<div className="relative flex-1 bg-white overflow-y-auto">
					<EmptyPeoples
						title="Unlock the Professional Directory"
						message="Upgrade your plan to access verified and pro contacts in the directory. Expand your network today!"
						withButton={true}
						buttonLabel="Upgrade Now"
						buttonProps={{
							startIcon: <ArrowCircleUpIcon />,
							onClick: () =>
								navigate({
									to: "/subscription",
								}),
						}}
					/>
				</div>
			</>
		);
	}
}

function RouteComponent() {
	const groupId = Route.useParams({
		select: (params) => params.id,
	});
	const navigate = Route.useNavigate();
	const [viewRef, inView] = useInView();
	const [search, setSearch] = useState("");
	const [memberIds, setMemberIds] = useState<number[]>([]);
	const debouncedSearch = useDebounce(search, 500);
	const updateGroup = useUpdateGroup();
	const { data: resGroup } = useSuspenseQuery(getGroupQueryOptions(groupId));
	const contacts = useContactPeoples({
		isDirectory: resGroup.group.directory,
		search: debouncedSearch,
		excludeProfile: true,
		estimateSize: 50,
	});
	const peoples = useMemo(() => {
		let items = cloneDeep(contacts.peoples || []);
		if (resGroup.group.members.length) {
			items = items.filter(
				(item) =>
					!resGroup.group.members.some((member) => member.id === item.id),
			);
		}
		if (resGroup.group.pending_invitations.length) {
			items = items.filter(
				(item) =>
					!resGroup.group.pending_invitations.some(
						(member) => member.id === item.id,
					),
			);
		}
		return items;
	}, [contacts.peoples, resGroup]);
	const onClickList = useCallback(
		(id: number) => {
			const newMemberIds = memberIds.includes(id)
				? memberIds.filter((memberId) => memberId !== id)
				: [...memberIds, id];
			setMemberIds(newMemberIds);
		},
		[memberIds],
	);
	const onClickAdd = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			if (!resGroup) return;
			return updateGroup.mutate(
				{
					id: resGroup.group.id,
					member_ids: memberIds,
				},
				{
					onSuccess: () => {
						toast.success("Contact Invited");
						return navigate({
							to: "/dashboard/chat/group/$id/$channelName",
							params: {
								id: resGroup.group.id,
								channelName: resGroup.group.channel_name,
							},
						});
					},
					onError: () => {
						toast.error("Failed to add member");
					},
				},
			);
		},
		[memberIds, updateGroup, resGroup, navigate],
	);
	useEffect(() => {
		if (!contacts.isFetchingNextPage && contacts.hasNextPage && inView) {
			contacts.fetchNextPage();
		}
	}, [
		contacts.fetchNextPage,
		contacts.hasNextPage,
		contacts.isFetchingNextPage,
		inView,
	]);
	return (
		<div className="relative bg-white min-h-full">
			<BackAppBar title="Add Member">
				<div className="container space-y-4">
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<Typography fontWeight={700} fontSize={14}>
						Contacts
					</Typography>
				</div>
			</BackAppBar>
			<FloatingLoader open={contacts.isLoading} />
			<div className="relative h-full my-4">
				<List disablePadding>
					{peoples.map((people) => (
						<ListItem
							key={people.id}
							className={cn({
								"!bg-primary-light": memberIds.includes(people.id),
							})}
							onClick={() => onClickList(people.id)}
						>
							<ListItemButton dense>
								<ListItemIcon>
									<Checkbox
										value={people.id}
										checked={memberIds.includes(people.id)}
										edge="start"
										tabIndex={-1}
										disableRipple
									/>
								</ListItemIcon>
								<ListItemAvatar>
									<Avatar
										alt={people.first_name || "L"}
										className="!bg-gray-light !text-dark"
										src={people.avatar ?? "/broken-image.jpg"}
									/>
								</ListItemAvatar>
								<ListItemText primary={people.fullname} />
							</ListItemButton>
						</ListItem>
					))}
					{!contacts.isFetchingNextPage && contacts.hasNextPage ? (
						<Skeleton ref={viewRef} className="h-12 w-full" />
					) : null}
				</List>
			</div>
			<div
				className={cn("fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3", {
					hidden: !memberIds.length,
				})}
			>
				<div className="wrapper">
					<div className="container grid grid-cols-2 items-center">
						<Typography fontWeight={700} fontSize={14} className="flex-1">
							{memberIds.length} People Selected
						</Typography>
						<BasicButton
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							onClick={onClickAdd}
							disabled={updateGroup.isPending}
						>
							Add
						</BasicButton>
					</div>
				</div>
			</div>
		</div>
	);
}
